import { useAppConfigContext, AppConstants } from '@ltvco/refresh-lib/ctx';
import {
  RadioPaymentsInline,
  TosInline,
  useProcessBraintree,
  useProcessCard,
  useProcessPayPal,
  useSubmitLegal,
  type PaymentMethodValue,
} from '@ltvco/refresh-lib/payments';
import { Text, Box } from '@ltvco/refresh-lib/theme';
import {
  closeSnackbar,
  enqueueSnackbar,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';

import { SUBSCRIPTION_PLAN_UNIQUE_NAME, PLAN_DISPLAY } from '../../constants';
import { SectionCard } from '../components';

import {
  TermsAndConditionsSection1,
  TermsAndConditionsSection2,
} from './components/TermsAndConditionsContent';

interface PaymentSectionProps {
  onSubscriptionComplete: () => void;
}

export const PaymentSection = ({
  onSubscriptionComplete,
}: PaymentSectionProps) => {
  const { logError } = useAppConfigContext();
  const { links, data } = useContext(AppConstants);
  const {
    session: { isAuthenticated },
    useAccount,
    setSession,
  } = useSession();
  const { refetch: refetchAccount } = useAccount(isAuthenticated);
  const processCardQuery = useProcessCard();
  const processBraintreeQuery = useProcessBraintree();
  const processPayPalQuery = useProcessPayPal();
  const updateLegalQuery = useSubmitLegal();

  // ToS state management
  const [tosAccepted, setTosAccepted] = useState(false);
  const [tosError, setTosError] = useState<string>('');
  const [activePayment] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethodValue | ''
  >('');

  // Use a ref to track the latest tosAccepted value to avoid stale closures
  const tosAcceptedRef = useRef(tosAccepted);

  // Keep the ref in sync with the state
  useEffect(() => {
    tosAcceptedRef.current = tosAccepted;
  }, [tosAccepted]);

  const defaultMutationParams = {
    plan: { unique_key: SUBSCRIPTION_PLAN_UNIQUE_NAME },
    source_of_payment: 'subscription_signup',
  };

  const onPaymentMethodAddedSuccess = async () => {
    closeSnackbar();
    const { data } = await refetchAccount();
    setSession({
      account: data,
    });
    onSubscriptionComplete();
  };

  const onPaymentMethodAddedError = (
    error: Error,
    paymentType:
      | 'Credit Card'
      | 'Apple Pay'
      | 'Google Pay'
      | 'PayPal'
      | 'Venmo',
  ) => {
    logError(`Subscription error with ${paymentType} payment method.`, error);

    enqueueSnackbar(
      'Your payment failed. Please try again or try another payment method.',
      { variant: 'error' },
    );
  };

  // ToS acceptance handler
  const handleAcceptTos = () => {
    setTosAccepted(true);
    setTosError('');
  };

  // Custom TOS content configuration
  const customTosContent = useMemo(
    () => ({
      sections: [
        {
          content: <TermsAndConditionsSection1 />,
          requiresCheckbox: true,
        },
        {
          content: <TermsAndConditionsSection2 />,
          requiresCheckbox: true,
        },
      ],
      validateAcceptance: (checkedStates: boolean[]) => {
        // Both checkboxes must be checked
        return checkedStates.length === 2 && checkedStates.every(Boolean);
      },
    }),
    [],
  );

  // Handler for when TOS acceptance state changes
  // Use useCallback to prevent unnecessary re-renders and state flickering
  const handleTosStateChange = useCallback((allAccepted: boolean) => {
    setTosAccepted(allAccepted);
    if (allAccepted) {
      setTosError('');
    }
  }, []);

  // TOS validation callback to be called on payment button click
  // Use ref to ensure we always check the latest tosAccepted value
  const handleTosValidation = useCallback((): boolean => {
    if (!tosAcceptedRef.current) {
      setTosError('You must accept the Terms of Service to continue.');
      return false;
    }
    setTosError('');
    return true;
  }, []);

  // Handler for when payment method changes - reset TOS state
  const handlePaymentMethodChange = useCallback(
    (paymentMethod: PaymentMethodValue | '') => {
      setSelectedPaymentMethod(paymentMethod);
      setTosAccepted(false);
      tosAcceptedRef.current = false;
      setTosError('');
    },
    [],
  );

  const showDefaultError = () => {
    enqueueSnackbar(
      'Something went wrong, please try again or contact customer service',
      { variant: 'error' },
    );
  };

  const handlePaymentMethodAdded = (
    paymentMethodID: number,
    paymentType:
      | 'Credit Card'
      | 'Apple Pay'
      | 'Google Pay'
      | 'PayPal'
      | 'Venmo',
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      updateLegalQuery.mutate(undefined, {
        onSuccess: () => {
          let mutationParams: Record<string, unknown>;
          let processQuery:
            | typeof processCardQuery
            | typeof processBraintreeQuery
            | typeof processPayPalQuery;

          if (paymentType === 'Credit Card') {
            mutationParams = {
              ...defaultMutationParams,
              cardId: paymentMethodID,
            };
            processQuery = processCardQuery;
          } else if (
            paymentType === 'Apple Pay' ||
            paymentType === 'Google Pay' ||
            paymentType === 'Venmo'
          ) {
            mutationParams = {
              ...defaultMutationParams,
              braintreeId: paymentMethodID,
            };
            processQuery = processBraintreeQuery;
          } else if (paymentType === 'PayPal') {
            mutationParams = {
              ...defaultMutationParams,
              paypalId: paymentMethodID,
            };
            processQuery = processPayPalQuery;
          } else {
            reject(new Error(`Invalid payment type: ${paymentType}`));
            return;
          }

          processQuery.mutate(mutationParams as never, {
            async onSuccess() {
              await onPaymentMethodAddedSuccess();
              resolve();
            },
            async onError(error: Error) {
              onPaymentMethodAddedError(error, paymentType);
              reject(error);
            },
          });
        },
        onError: () => {
          showDefaultError();
          reject(new Error('Failed to update legal'));
        },
      });
    });
  };

  // Memoize the TOS component to prevent unnecessary re-renders
  // Use selectedPaymentMethod as key to force remount when payment method changes
  const memoizedTosComponent = useMemo(
    () => (
      <Box sx={{ mb: 3 }}>
        <TosInline
          key={selectedPaymentMethod}
          showCheckbox={true}
          acceptTos={handleAcceptTos}
          links={links}
          brandData={data}
          activePayment={activePayment}
          showButton={false}
          headline={<></>}
          customTosContent={customTosContent}
          setParentTosAccepted={handleTosStateChange}
        />
        {tosError && (
          <Text color="error" variant="body2" sx={{ mt: 1, pl: 2 }}>
            {tosError}
          </Text>
        )}
      </Box>
    ),
    [
      selectedPaymentMethod,
      handleAcceptTos,
      links,
      data,
      activePayment,
      customTosContent,
      handleTosStateChange,
      tosError,
    ],
  );

  return (
    <>
      <SectionCard variant="outlined" sx={{ p: { xs: 1, md: 3 }, mt: 3 }}>
        <RadioPaymentsInline
          onPaymentMethodAdded={handlePaymentMethodAdded}
          onTosValidation={handleTosValidation}
          onPaymentMethodChange={handlePaymentMethodChange}
          tosComponent={memoizedTosComponent}
          applePayAmount={PLAN_DISPLAY.setupPrice.replace('$', '')}
        />
      </SectionCard>
    </>
  );
};
